import React, { useEffect, useCallback, useContext, useMemo, MouseEventHandler, useState } from 'react'
import { connect } from 'react-redux'
import { useAppDispatch } from 'hooks/reduxHooks'
import { fetchNotificationsForSlug, dismissNotificationBanner } from 'actions/ContentActions'
import { SITEWIDE_NOTIFICATION_SLUG } from 'constants/content'
import Image from 'components/Common/Image'
import GeoContext from 'contexts/geoContext'
import PageBanner from 'LuxKit/Banners/PageBanner'
import { getNotificationBannerCookieName } from 'cookies/notificationBannerCookie'
import { encode, normalizeBase64 } from 'lib/encoding/isomorphicBase64'
import config from 'constants/config'
import { isEnabledForFeature } from 'lib/config/featureFlagUtils'
import CookiesContext from 'contexts/CookiesContext'
import TextLink from 'LuxKit/TextLink'
import { useCurrentSiteTakeover } from 'takeovers/utils/hooks/useCurrentSiteTakeover'
import { SOCIETE_DOUBLE_POINTS_TAKEOVER_ID } from 'takeovers/societeDoublePointsTakeover/constants/societeDoublePointsTakeoverConstants'
import { LUX_PLUS_EXPERIENCE_DISCOUNT_TAKEOVER_ID } from 'takeovers/luxplusExperienceDiscountTakeover/constants/luxPlusExperienceDiscountTakeoverConstants'
import usePreferredRetailStore from 'hooks/usePreferredRetailStore'
import { getRetailStoreContent, STORE_CAMPAIGN_URL, RetailStoreType } from 'constants/retailStores'

interface Props {
  notificationBanner: App.NotificationBanner | null
  slug?: string
  overrideBanner?: App.NotificationBanner | ((regionCode: string) => App.NotificationBanner)
}

function createStoreBanner(storeType: RetailStoreType): App.NotificationBanner {
  const store = getRetailStoreContent(storeType)
  return {
    bannerText: `Book your next escape in-store at Luxury Escapes, ${store.title.replace(' Retail Store', '')}`,
    linkText: 'BOOK NOW',
    linkTo: STORE_CAMPAIGN_URL,
    openInNewTab: false,
    id: 'defaultNotificationBanner',
    slug: 'defaultNotificationBanner',
    show: true,
  }
}

// Date when store banners should reappear after holiday period
const STORE_BANNERS_REAPPEAR_DATE = new Date('2026-01-05T00:01:00')

function areStoreBannersHiddenForHoliday(): boolean {
  return new Date() < STORE_BANNERS_REAPPEAR_DATE
}

function NotificationBanner(props: Props) {
  const {
    slug = SITEWIDE_NOTIFICATION_SLUG,
    notificationBanner,
    overrideBanner,
  } = props
  const dispatch = useAppDispatch()

  const { getCookie } = useContext(CookiesContext)
  const geo = useContext(GeoContext)
  const [isDismissed, setIsDismissed] = useState(false)
  const currentTakeover = useCurrentSiteTakeover()
  const shouldHideForTakeover = currentTakeover?.id === SOCIETE_DOUBLE_POINTS_TAKEOVER_ID ||
    currentTakeover?.id === LUX_PLUS_EXPERIENCE_DISCOUNT_TAKEOVER_ID
  const preferredStore = usePreferredRetailStore()

  const banner = useMemo(() => {
    if (overrideBanner) {
      return typeof overrideBanner === 'function'
        ? overrideBanner(geo.currentRegionCode)
        : overrideBanner
    }

    if (slug === SITEWIDE_NOTIFICATION_SLUG || slug === 'none') {
      // Hide store banners during holiday period until they should reappear
      if (areStoreBannersHiddenForHoliday()) {
        return notificationBanner
      }

      if (preferredStore && isEnabledForFeature(config.CHADSTONE_STORE_BANNER_REGIONS, geo.currentRegionCode)) {
        const notificationBannerCookie = getCookie(getNotificationBannerCookieName(slug)) || ''

        // Victorian and New South Welsh users get dedicated store banners. We still show
        // these even when no site-wide banner exists, but respect dismissals via the cookie.
        const storeBanner = createStoreBanner(preferredStore)
        const encodedBannerText = encode(storeBanner.bannerText)
        return {
          ...storeBanner,
          show: encodedBannerText !== null && normalizeBase64(encodedBannerText) !== normalizeBase64(notificationBannerCookie),
        }
      }
    }

    return notificationBanner
  }, [slug, geo.currentRegionCode, notificationBanner, preferredStore, getCookie, overrideBanner])

  const effectiveSlug = banner?.slug || slug

  useEffect(() => {
    // Don't fetch from CMS if we have an override banner
    if (!overrideBanner && effectiveSlug) {
      dispatch(fetchNotificationsForSlug(effectiveSlug, getCookie))
    }
  }, [dispatch, effectiveSlug, getCookie, overrideBanner])

  const onDismiss = useCallback(() => {
    if (banner) {
      dispatch(dismissNotificationBanner(effectiveSlug, banner.bannerText))
      setIsDismissed(true)
    }
  }, [banner, dispatch, effectiveSlug])

  const handleLinkClick = useCallback<MouseEventHandler<HTMLAnchorElement>>((e) => {
    if (banner?.openInPopup) {
      e.preventDefault()

      window.open(banner.linkTo, 'popup', 'width=640,height=640')
    }
  }, [banner])

  if (isDismissed || !banner?.show || shouldHideForTakeover) {
    return null
  }

  const { openInNewTab, openInPopup, bannerText, linkText, linkTo, image } = banner
  const isExternalLink = linkTo?.startsWith('https://')

  return <PageBanner
    id="notification-page-banner"
    className="no-print"
    data-testid="notification-page-banner"
    kind="promotional-primary"
    icon={image ? <div><Image id={image.id} width={image.width} alt={image.alt} enhancedFormats={false} quality="best" /></div> : undefined}
    onDismiss={onDismiss}
    tabletAlign="center"
  >
    {bannerText}
    {(linkTo && linkText) && <>
      {' '}
      <TextLink
        rel="noopener"
        size="medium"
        target={openInNewTab ? '_blank' : (openInPopup ? 'popup' : undefined)}
        onClick={handleLinkClick}
        // To prevent auto-append current region to external links
        to={!isExternalLink ? linkTo : undefined}
        href={isExternalLink ? linkTo : undefined}
      >
        {linkText}
      </TextLink>
    </>}
  </PageBanner>
}

function mapStateToProps(state: App.State, ownProps: Partial<Props>) {
  return {
    notificationBanner: state.content.notifications[ownProps.slug || SITEWIDE_NOTIFICATION_SLUG],
  }
}

export default connect(mapStateToProps)(NotificationBanner)
